/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React, { useRef } from 'react';
import { Button } from '@radix-ui/themes';
import { RefreshCw } from 'lucide-react';

import { ConfirmDialog } from '../../../../shared/form/ConfirmDialog';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';

export interface RepositoryReevaluateActionProps {
  repositoryName: string;
  /** A re-evaluation of the repository is running, so another cannot start. */
  disabled: boolean;
  confirmOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onConfirm: () => void;
}

/** The Re-evaluate Repository action of a list row: a button and the confirmation it asks for. */
export function RepositoryReevaluateAction({
  repositoryName,
  disabled,
  confirmOpen,
  onOpen,
  onClose,
  onConfirm,
}: RepositoryReevaluateActionProps): JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { confirm } = STRINGS.reevaluate;

  // The dialog has no Radix trigger, so it cannot return focus itself: put it back on the button once it is gone.
  const onDialogOpenChange = (open: boolean) => {
    if (open) {
      onOpen();
      return;
    }
    onClose();
    setTimeout(() => triggerRef.current?.focus(), 0);
  };

  return (
    <>
      {/* aria-disabled, not disabled: a disabled button cannot keep focus, so focus is lost when a run starts. */}
      <Button
        ref={triggerRef}
        size="1"
        variant="soft"
        aria-label={STRINGS.reevaluate.buttonLabel(repositoryName)}
        aria-haspopup="dialog"
        aria-expanded={confirmOpen}
        aria-disabled={disabled ? true : undefined}
        data-disabled={disabled ? true : undefined}
        onClick={() => {
          if (!disabled) {
            onOpen();
          }
        }}
        data-analytics-id="nxrm-security-evaluations-reevaluate-btn"
      >
        <RefreshCw size={14} aria-hidden="true" />
        {STRINGS.reevaluate.button}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={onDialogOpenChange}
        title={confirm.title}
        message={confirm.body(repositoryName)}
        confirmLabel={confirm.confirmLabel}
        cancelLabel={confirm.cancelLabel}
        variant="confirm"
        onConfirm={onConfirm}
        testId="repository-reevaluate-dialog"
        analyticsId="nxrm-security-evaluations-reevaluate-confirm-btn"
        cancelAnalyticsId="nxrm-security-evaluations-reevaluate-cancel-btn"
      />
    </>
  );
}
